import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageSquare, Send, Sparkles, Loader2, AlertCircle, Smartphone, Zap, MessageCircle, Smile, Briefcase, FileText } from 'lucide-react';
// Add this helper function at the top of the file, before the component
const getChatStorageKey = (transcriptId: string) => `transcript-chat-${transcriptId}`;
const saveChatHistory = (transcriptId: string, messages: ChatMessage[]) => {
  try {
    const key = getChatStorageKey(transcriptId);
    localStorage.setItem(key, JSON.stringify({
      messages,
      timestamp: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Failed to save chat history:', error);
  }
};
const loadChatHistory = (transcriptId: string): ChatMessage[] => {
  try {
    const key = getChatStorageKey(transcriptId);
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const data = JSON.parse(stored);
    // Convert timestamp strings back to Date objects
    return data.messages.map((msg: any) => ({
      ...msg,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : undefined
    }));
  } catch (error) {
    console.error('Failed to load chat history:', error);
    return [];
  }
};
const clearChatHistory = (transcriptId: string) => {
  try {
    const key = getChatStorageKey(transcriptId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear chat history:', error);
  }
};
interface TranscriptContext {
  content: string;
  title?: string;
  description?: string;
  channel_name?: string;
  platform?: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  repost_count?: number;
  upload_date?: string;
  duration?: number;
  quote?: string;
  tldr?: string[];
  top_comments?: Array<{
    text: string;
    likes: number;
  }>;
}
interface TranscriptChatProps {
  transcript: TranscriptContext;
  initialQuestion?: string | null;
  transcriptId: string; // NEW: Required for storage
}
interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
}
type ContentType = 'relationship' | 'educational' | 'news' | 'howto' | 'entertainment' | 'general';
type FollowUpAction = {
  label: string;
  prompt: string;
  icon?: React.ReactNode;
};
// Content-type specific example questions
const EXAMPLE_QUESTIONS_BY_TYPE: Record<ContentType, string[]> = {
  relationship: ['How can I apply this to my situation?', 'Help me respond to this', "What's the key advice here?", 'How do I start this conversation?'],
  educational: ['Break this down simply', 'Quiz me on the key concepts', 'What are the prerequisites?', "Explain like I'm 5"],
  news: ["What's the other perspective?", 'Fact-check the main claims', 'Summarize in 3 bullets', "What's the context?"],
  howto: ['What are the exact steps?', 'What materials do I need?', 'Show me an example', 'What could go wrong?'],
  entertainment: ["What's the main point?", 'Why is this trending?', "What's the best part?", 'What are people saying?'],
  general: ["What's the main point?", 'Why is this trending?', 'What are the key takeaways?', 'Summarize this']
};
// Follow-up actions based on response type
const FOLLOW_UP_ACTIONS: Record<string, FollowUpAction[]> = {
  draft: [{
    label: 'Make it shorter',
    prompt: 'Make that response more concise',
    icon: <Zap size={12} data-id="element-1584" />
  }, {
    label: 'More casual',
    prompt: 'Rewrite that in a more casual, friendly tone',
    icon: <Smile size={12} data-id="element-1585" />
  }, {
    label: 'Add emoji',
    prompt: 'Add appropriate emojis to make it warmer',
    icon: <Smile size={12} data-id="element-1586" />
  }, {
    label: 'More formal',
    prompt: 'Make that more professional and formal',
    icon: <Briefcase size={12} data-id="element-1587" />
  }],
  explanation: [{
    label: 'Simpler',
    prompt: 'Explain that more simply',
    icon: <Zap size={12} data-id="element-1588" />
  }, {
    label: 'More detail',
    prompt: 'Give me more details about that',
    icon: <MessageCircle size={12} data-id="element-1589" />
  }, {
    label: 'Examples',
    prompt: 'Give me specific examples',
    icon: <Sparkles size={12} data-id="element-1590" />
  }],
  general: [{
    label: 'Tell me more',
    prompt: 'Tell me more about that',
    icon: <MessageCircle size={12} data-id="element-1591" />
  }, {
    label: 'Simplify',
    prompt: 'Explain that more simply',
    icon: <Zap size={12} data-id="element-1592" />
  }]
};
export const TranscriptChat: React.FC<TranscriptChatProps> = ({
  transcript,
  initialQuestion,
  transcriptId
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentType, setContentType] = useState<ContentType>('general');
  const [isDetectingContent, setIsDetectingContent] = useState(true);
  const [lastResponseType, setLastResponseType] = useState<string>('general');
  const [showFollowUps, setShowFollowUps] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);
  // Load chat history on mount
  useEffect(() => {
    const savedMessages = loadChatHistory(transcriptId);
    if (savedMessages.length > 0) {
      setMessages(savedMessages);
      // Don't show follow-ups for old messages
      setShowFollowUps(false);
    }
  }, [transcriptId]);
  // Save chat history whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(transcriptId, messages);
    }
  }, [messages, transcriptId]);
  const scrollToBottom = () => {
    if (shouldScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
      shouldScrollRef.current = false;
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Detect content type on mount using OpenAI
  useEffect(() => {
    const detectContentType = async () => {
      try {
        const detectionPrompt = `Analyze this video and classify it into ONE category:
- relationship: advice about relationships, communication, dating, marriage
- educational: teaching, tutorials, explaining concepts, academic content
- news: current events, commentary, analysis, journalism
- howto: step-by-step guides, DIY, recipes, instructions
- entertainment: comedy, reactions, stories, viral content

Video Title: ${transcript.title || 'Unknown'}
Description: ${transcript.description || 'None'}
Key Quote: ${transcript.quote || 'None'}

Respond with ONLY the category name, nothing else.`;
        const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: 'gpt-4-turbo',
            messages: [{
              role: 'system',
              content: 'You are a content classifier. Respond with only the category name.'
            }, {
              role: 'user',
              content: detectionPrompt
            }],
            temperature: 0.3,
            max_tokens: 20
          })
        });
        if (response.ok) {
          const data = await response.json();
          const detectedType = data.choices[0].message.content.trim().toLowerCase() as ContentType;
          if (['relationship', 'educational', 'news', 'howto', 'entertainment'].includes(detectedType)) {
            setContentType(detectedType);
          }
        }
      } catch (err) {
        console.error('Content detection failed:', err);
      } finally {
        setIsDetectingContent(false);
      }
    };
    detectContentType();
  }, [transcript.title, transcript.description, transcript.quote]);
  const buildSystemPrompt = (data: TranscriptContext): string => {
    let prompt = `You are a helpful AI assistant analyzing a video. Use the following context to answer user questions accurately.\n\n`;
    prompt += `VIDEO INFORMATION:\n`;
    if (data.title) prompt += `- Title: ${data.title}\n`;
    if (data.channel_name) prompt += `- Creator: ${data.channel_name}\n`;
    if (data.platform) prompt += `- Platform: ${data.platform}\n`;
    if (data.upload_date) prompt += `- Upload Date: ${data.upload_date}\n`;
    if (data.duration) prompt += `- Duration: ${data.duration} seconds\n`;
    prompt += `\nENGAGEMENT METRICS:\n`;
    if (data.view_count) prompt += `- Views: ${data.view_count.toLocaleString()}\n`;
    if (data.like_count) prompt += `- Likes: ${data.like_count.toLocaleString()}\n`;
    if (data.comment_count) prompt += `- Comments: ${data.comment_count.toLocaleString()}\n`;
    if (data.repost_count) prompt += `- Reposts: ${data.repost_count.toLocaleString()}\n`;
    if (data.quote) prompt += `\nKEY QUOTE: "${data.quote}"\n`;
    if (data.tldr && data.tldr.length > 0) {
      prompt += `\nKEY TAKEAWAYS (TLDR):\n`;
      data.tldr.forEach(item => prompt += `- ${item}\n`);
    }
    if (data.description) {
      prompt += `\nVIDEO DESCRIPTION:\n${data.description}\n`;
    }
    // Check if we have actual transcript content vs just title
    const hasTranscript = data.content && data.content.trim().length > 0 && data.content !== data.title;
    if (hasTranscript) {
      prompt += `\nTRANSCRIPT CONTENT:\n${data.content}\n`;
    } else {
      prompt += `\nNOTE: Full transcript is not available. Answer questions based on the video title, engagement metrics, and any other available context.\n`;
    }
    if (data.top_comments && data.top_comments.length > 0) {
      prompt += `\nTOP COMMENTS:\n`;
      data.top_comments.slice(0, 5).forEach((comment, i) => {
        prompt += `${i + 1}. "${comment.text}" (${comment.likes} likes)\n`;
      });
    }
    prompt += `\nINSTRUCTIONS:\n`;
    prompt += `1. Answer based on the available context (title, description, quote, key takeaways, engagement metrics, and transcript if available).\n`;
    prompt += `2. Be concise and direct.\n`;
    prompt += `3. Use markdown formatting for better readability (bold, italic, lists).\n`;
    prompt += `4. If asked about sentiment or reception, use the engagement metrics and comments.\n`;
    prompt += `5. If the specific information isn't available, acknowledge this and offer insights based on what IS available (e.g., analyze the title, discuss what the topic typically involves, or interpret engagement patterns).\n`;
    prompt += `6. When transcript is unavailable, you can still discuss: the topic based on the title, typical content in this category, what the engagement metrics suggest about reception, etc.\n`;
    return prompt;
  };
  const detectResponseType = (content: string): string => {
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes("here's a") && (lowerContent.includes('message') || lowerContent.includes('response'))) {
      return 'draft';
    }
    if (lowerContent.includes('explain') || lowerContent.includes('because') || lowerContent.includes('the reason')) {
      return 'explanation';
    }
    return 'general';
  };
  const handleSendMessage = async (question?: string) => {
    const messageText = question || input.trim();
    if (!messageText || isLoading) return;
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    setShowFollowUps(false);
    shouldScrollRef.current = true;
    try {
      const systemPrompt = buildSystemPrompt(transcript);
      const conversationHistory = [{
        role: 'system',
        content: systemPrompt
      }, ...messages.map(m => ({
        role: m.role,
        content: m.content
      })), {
        role: 'user',
        content: messageText
      }];
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: conversationHistory,
          stream: false,
          temperature: 0.7,
          max_tokens: 500
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API request failed with status ${response.status}`);
      }
      const data = await response.json();
      const assistantContent = data.choices[0].message.content;
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      // Detect response type and show follow-ups
      const responseType = detectResponseType(assistantContent);
      setLastResponseType(responseType);
      setShowFollowUps(true);
      shouldScrollRef.current = false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get response');
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleFollowUpClick = (action: FollowUpAction) => {
    setShowFollowUps(false);
    handleSendMessage(action.prompt);
  };
  const handleExampleClick = (question: string) => {
    handleSendMessage(question);
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const exampleQuestions = EXAMPLE_QUESTIONS_BY_TYPE[contentType];
  const followUpActions = FOLLOW_UP_ACTIONS[lastResponseType] || FOLLOW_UP_ACTIONS.general;
  useEffect(() => {
    if (initialQuestion && messages.length === 0 && !isLoading) {
      handleSendMessage(initialQuestion);
    }
  }, [initialQuestion]);
  const handleClearHistory = () => {
    if (window.confirm('Clear this conversation? This cannot be undone.')) {
      setMessages([]);
      clearChatHistory(transcriptId);
      setShowFollowUps(false);
    }
  };
  return <Card className="h-full flex flex-col" data-id="element-1593">
      <div className="flex items-center justify-between p-4 border-b border-border/40 flex-shrink-0" data-id="element-1594">
        <h2 className="font-semibold text-base flex items-center" data-id="element-1595">
          <MessageSquare size={16} className="mr-2 text-primary" data-id="element-1596" />
          Chat about this transcript
        </h2>
        <div className="flex items-center gap-2" data-id="element-1597">
          <div className="flex items-center text-xs text-muted-foreground" data-id="element-1598">
            <Sparkles size={14} className="mr-1 text-primary" data-id="element-1599" />
            <span data-id="element-1600">AI-powered</span>
          </div>
          {messages.length > 0 && <Button variant="ghost" size="sm" onClick={handleClearHistory} className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive" data-id="element-1601">
              Clear
            </Button>}
        </div>
      </div>

      {/* Messages Area - Scrollable, takes remaining space */}
      <div className="flex-1 overflow-y-auto scrollbar-hide p-4 min-h-0" data-id="element-1602">
        {messages.length > 0 ? <div className="space-y-4" data-id="element-1603">
            {/* Restored conversation indicator */}
            <div className="flex justify-center" data-id="element-1604">
              <span className="text-xs text-muted-foreground bg-secondary/50 px-3 py-1 rounded-full" data-id="element-1605">
                Previous conversation restored
              </span>
            </div>

            {messages.map((message, index) => <div key={index} data-id="element-1606">
                <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1607">
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`} data-id="element-1608">
                    {message.role === 'assistant' ? <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none" data-id="element-1609">
                        <ReactMarkdown components={{
                  p: ({
                    children
                  }) => <p className="mb-2 last:mb-0" data-id="element-1611">{children}</p>,
                  strong: ({
                    children
                  }) => <strong className="font-semibold" data-id="element-1612">
                                {children}
                              </strong>,
                  em: ({
                    children
                  }) => <em className="italic" data-id="element-1613">{children}</em>,
                  code: ({
                    children
                  }) => <code className="bg-secondary/50 px-1.5 py-0.5 rounded text-xs font-mono" data-id="element-1614">
                                {children}
                              </code>,
                  ul: ({
                    children
                  }) => <ul className="list-disc list-inside space-y-1 my-2" data-id="element-1615">
                                {children}
                              </ul>,
                  ol: ({
                    children
                  }) => <ol className="list-decimal list-inside space-y-1 my-2" data-id="element-1616">
                                {children}
                              </ol>,
                  li: ({
                    children
                  }) => <li className="ml-2" data-id="element-1617">{children}</li>,
                  h1: ({
                    children
                  }) => <h1 className="text-lg font-bold mb-2" data-id="element-1618">
                                {children}
                              </h1>,
                  h2: ({
                    children
                  }) => <h2 className="text-base font-bold mb-2" data-id="element-1619">
                                {children}
                              </h2>,
                  h3: ({
                    children
                  }) => <h3 className="text-sm font-bold mb-1" data-id="element-1620">
                                {children}
                              </h3>,
                  blockquote: ({
                    children
                  }) => <blockquote className="border-l-2 border-primary/30 pl-3 italic my-2" data-id="element-1621">
                                {children}
                              </blockquote>
                }} data-id="element-1610">
                          {message.content}
                        </ReactMarkdown>
                      </div> : <p className="text-sm whitespace-pre-wrap leading-relaxed" data-id="element-1622">
                        {message.content}
                      </p>}
                    {message.timestamp && <span className={`text-[10px] mt-1 block ${message.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} data-id="element-1623">
                        {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                      </span>}
                  </div>
                </div>

                {/* Follow-up action chips after assistant response */}
                {message.role === 'assistant' && index === messages.length - 1 && showFollowUps && !isLoading && <div className="flex flex-wrap gap-2 mt-2 ml-2" data-id="element-1624">
                      {followUpActions.map((action, idx) => <Button key={idx} variant="outline" size="sm" onClick={() => handleFollowUpClick(action)} className="text-xs rounded-full h-7 px-3 bg-background hover:bg-secondary/50" data-id="element-1625">
                          {action.icon && <span className="mr-1" data-id="element-1626">{action.icon}</span>}
                          {action.label}
                        </Button>)}
                    </div>}

                {/* SMS Share CTA after helpful draft responses */}
                {message.role === 'assistant' && index === messages.length - 1 && lastResponseType === 'draft' && !isLoading && <div className="mt-3 ml-2 p-3 bg-primary/5 border border-primary/20 rounded-lg" data-id="element-1627">
                      <div className="flex items-start gap-2" data-id="element-1628">
                        <Smartphone size={16} className="text-primary mt-0.5 flex-shrink-0" data-id="element-1629" />
                        <div className="flex-1" data-id="element-1630">
                          <p className="text-xs font-medium text-primary mb-1" data-id="element-1631">
                            Want to send this via SMS?
                          </p>
                          <p className="text-[10px] text-muted-foreground mb-2" data-id="element-1632">
                            Text{' '}
                            <span className="font-mono font-semibold" data-id="element-1633">
                              (774) 472-7423
                            </span>{' '}
                            with this transcript link
                          </p>
                          <Button variant="outline" size="sm" onClick={() => {
                  const smsText = `sms:(774)472-7423&body=Check out this video: ${window.location.href}`;
                  window.location.href = smsText;
                }} className="text-xs h-7 rounded-full" data-id="element-1634">
                            <Smartphone size={12} className="mr-1" data-id="element-1635" />
                            Open SMS App
                          </Button>
                        </div>
                      </div>
                    </div>}
              </div>)}

            {isLoading && <div className="flex justify-start" data-id="element-1636">
                <div className="bg-secondary text-secondary-foreground rounded-2xl px-4 py-3 flex items-center gap-2" data-id="element-1637">
                  <Loader2 size={16} className="animate-spin" data-id="element-1638" />
                  <span className="text-xs text-muted-foreground" data-id="element-1639">
                    Thinking...
                  </span>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-1640" />
          </div> : <div className="h-full flex flex-col justify-center" data-id="element-1641">
            <p className="text-sm text-muted-foreground mb-3" data-id="element-1642">
              {isDetectingContent ? <span className="flex items-center gap-2" data-id="element-1643">
                  <Loader2 size={14} className="animate-spin" data-id="element-1644" />
                  Analyzing content...
                </span> : `Try asking about ${transcript.title ? `"${transcript.title}"` : 'this video'}:`}
            </p>
            {!isDetectingContent && <div className="flex flex-wrap gap-2 justify-center" data-id="element-1645">
                {exampleQuestions.map((question, index) => <Button key={index} variant="outline" size="sm" onClick={() => handleExampleClick(question)} disabled={isLoading} className="text-xs rounded-full bg-background hover:bg-secondary/50" data-id="element-1646">
                    {question}
                  </Button>)}
              </div>}
          </div>}
      </div>

      {/* Fixed Input Area at Bottom */}
      <div className="border-t border-border/40 p-4 flex-shrink-0 bg-background" data-id="element-1647">
        {/* Error Message */}
        {error && <div className="mb-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start" data-id="element-1648">
            <AlertCircle size={16} className="text-destructive mr-2 mt-0.5 flex-shrink-0" data-id="element-1649" />
            <p className="text-sm text-destructive" data-id="element-1650">{error}</p>
          </div>}

        {/* Input */}
        <div className="flex gap-2 items-end" data-id="element-1651">
          <Input value={input} onChange={e => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask a question about this video..." disabled={isLoading} className="flex-1 min-h-[44px]" data-id="element-1652" />
          <Button onClick={() => handleSendMessage()} disabled={!input.trim() || isLoading} size="icon" className="rounded-full h-11 w-11 flex-shrink-0" data-id="element-1653">
            {isLoading ? <Loader2 size={18} className="animate-spin" data-id="element-1654" /> : <Send size={18} data-id="element-1655" />}
          </Button>
        </div>

        {/* Info Text */}
        <p className="text-[10px] text-muted-foreground mt-2 text-center opacity-70" data-id="element-1656">
          AI can make mistakes. Check important info.
        </p>
      </div>
    </Card>;
};